/**
 * Product Intelligence Service
 * API service and types for the Product Intelligence Tracker
 */

import { apiService } from './api';

// Types
export interface DevNote {
  label: string;
  value: string;
}

export interface ProductIntelligenceItem {
  id: number;
  item_id: string;
  category: 'bug' | 'improvement' | 'marketing';
  title: string;
  description: string | null;
  status: string;
  priority: string | null;
  subcategory: string | null;
  reporter: string | null;
  assignee: string | null;
  dev_notes: DevNote[];
  target_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductIntelligenceListResponse {
  items: ProductIntelligenceItem[];
  total: number;
}

export interface UpdateItemPayload {
  status?: string;
  priority?: string;
  assignee?: string;
  add_note?: DevNote;
}

export interface CreateItemPayload {
  category: 'bug' | 'improvement' | 'marketing';
  title: string;
  description?: string;
  status: string;
  priority?: string;
  subcategory?: string;
  reporter?: string;
  assignee?: string;
  target_date?: string;
}

export interface Attachment {
  id: number;
  item_id: string;
  filename: string;
  original_name: string;
  s3_key: string;
  file_size: number;
  mime_type: string;
  uploaded_by: string | null;
  download_url: string;
  created_at: string;
}

// Status options per category
export const STATUS_OPTIONS: Record<string, string[]> = {
  bug: ['Open', 'In Progress', 'Resolved', 'Deployed - Needs Testing', 'Deployed - Passed Testing'],
  improvement: ['Backlog', 'Scoped', 'In Progress', 'Resolved', 'Deployed - Needs Testing', 'Deployed - Passed Testing'],
  marketing: ['Idea', 'In Progress', 'Active', 'Paused'],
};

export const PRIORITY_OPTIONS = ['P0', 'P1', 'P2', 'P3'];

// API service
const productIntelligenceService = {
  async getItems(
    category: string,
    filters?: { status?: string; priority?: string; search?: string }
  ): Promise<ProductIntelligenceListResponse> {
    const params = new URLSearchParams({ category });
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.search) params.append('search', filters.search);

    const response = await apiService.get<ProductIntelligenceListResponse>(
      `/api/admin/product-intelligence?${params.toString()}`
    );
    return response.data;
  },

  async getItem(itemId: string): Promise<ProductIntelligenceItem> {
    const response = await apiService.get<ProductIntelligenceItem>(
      `/api/admin/product-intelligence/${itemId}`
    );
    return response.data;
  },

  async updateItem(itemId: string, updates: UpdateItemPayload): Promise<ProductIntelligenceItem> {
    const response = await apiService.patch<ProductIntelligenceItem>(
      `/api/admin/product-intelligence/${itemId}`,
      updates
    );
    return response.data;
  },

  async createItem(payload: CreateItemPayload): Promise<ProductIntelligenceItem> {
    const response = await apiService.post<ProductIntelligenceItem>(
      '/api/admin/product-intelligence',
      payload
    );
    return response.data;
  },

  async uploadAttachment(itemId: string, file: File): Promise<Attachment> {
    return apiService.uploadFile(
      `/api/admin/product-intelligence/${itemId}/attachments`,
      file
    );
  },

  async getAttachments(itemId: string): Promise<Attachment[]> {
    const response = await apiService.get<Attachment[]>(
      `/api/admin/product-intelligence/${itemId}/attachments`
    );
    return response.data;
  },

  async deleteAttachment(itemId: string, attachmentId: number): Promise<void> {
    await apiService.delete(
      `/api/admin/product-intelligence/${itemId}/attachments/${attachmentId}`
    );
  },
};

export default productIntelligenceService;
